import * as Popover from '@radix-ui/react-popover'
import { ChevronDown, ExternalLink } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
// Leaf component with no admin coupling — it only resolves an authored `icon`
// value to a mark. Living under `admin/` is an accident of where it was first
// needed; moving it to `components/ui/` would rewrite 23 unrelated imports, so
// it is left where it is and imported as-is.
import { AuthoredIcon } from '@/components/admin/shared/authored-icon'
// The database-free leaf, never `problem-sources.ts` — that one imports
// Drizzle, and this component ships to the browser.
import {
  type AttributionCopyrightScope,
  type AttributionSource,
  copyrightScopeFor,
  hasAttribution,
  type ProblemAttribution,
} from '@/lib/problems/attribution'
import { trpc } from '@/lib/trpc'

/**
 * How long the popover survives the pointer leaving it.
 *
 * The trigger and the panel are separate elements with a gap between them, so
 * without a grace period the popover closes while the pointer crosses that gap
 * and its links become unreachable by mouse.
 */
const LEAVE_DELAY_MS = 150

/**
 * Nominative-use notice: naming and linking a site is not a claim of any
 * relationship with it.
 *
 * It once opened by asserting BCTCI's statement, examples and test cases are
 * original. That sentence was removed (Mike, LG-409): it is a claim rather
 * than a disclaimer, and a claim we would have to defend problem by problem if
 * one of ours ever read too close to the wording it was written from. What is
 * left disclaims a relationship and asserts nothing.
 */
const DISCLAIMER = 'Not affiliated with or endorsed by the sites listed.'

/**
 * How many marks the collapsed cluster draws before the rest become `+N`.
 *
 * Four is where overlapping 14px marks still read as distinct sites rather
 * than a smear, and it is well clear of what the corpus actually carries — no
 * problem today lists more than a couple of other places to practise it. The
 * overflow is insurance against a future import, not a routine state.
 */
const MAX_CLUSTER_MARKS = 4

function OriginalPill() {
  return (
    <span className="rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-semibold tracking-wide text-blue-700 dark:bg-blue-950 dark:text-blue-300">
      ORIGINAL
    </span>
  )
}

function PremiumPill() {
  return (
    <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold tracking-wide text-amber-700 dark:bg-amber-950 dark:text-amber-300">
      PREMIUM
    </span>
  )
}

/**
 * One credited source.
 *
 * Renders as an anchor when the source can be linked and as a plain row when
 * it cannot — a book has no URL on either the row or the site, and neither do
 * some company-backed sources, and a link to nowhere is worse than no link.
 */
function SourceRow({
  source,
  isOriginal,
}: {
  source: AttributionSource
  isOriginal: boolean
}) {
  const body = (
    <>
      <AuthoredIcon
        icon={source.icon}
        size="sm"
        className="shrink-0"
        fallbackChip
      />
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1.5">
          <span className="truncate font-medium">{source.name}</span>
          {isOriginal && <OriginalPill />}
          {source.premiumRequired && <PremiumPill />}
        </span>
        {source.publishedAs && (
          <span className="text-muted-foreground block truncate text-xs">
            {isOriginal ? 'published as' : 'as'} “{source.publishedAs}”
          </span>
        )}
        {/* No "in the book" caption. It was printed for every source with no
            resolvable URL, and "no URL" does not mean "book": company-backed
            interview-experience sources are created without one, so a row read
            "Google Interview Experience · in the book". Nothing on `Source`
            distinguishes a book — `category` is only prep_resource or
            interview_experience — so the claim cannot be made truthfully, and
            an unsupported claim is the one thing this widget must not print.
            The missing link and glyph already say the row is not clickable. */}
      </span>
      {source.url && (
        <ExternalLink
          aria-hidden="true"
          className="text-muted-foreground h-3.5 w-3.5 shrink-0"
        />
      )}
    </>
  )

  const rowClass = 'flex items-center gap-2 rounded px-2 py-1.5 text-sm'

  if (!source.url) {
    return <div className={rowClass}>{body}</div>
  }

  return (
    <a
      href={source.url}
      target="_blank"
      rel="noopener noreferrer"
      className={`${rowClass} hover:bg-muted focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none`}
    >
      {body}
    </a>
  )
}

/**
 * The description panel's source credit: a pill naming who to credit, opening
 * a panel that lists everywhere else the problem can be practised.
 *
 * Presentational — takes the attribution it renders, so the admin problem
 * editor can preview unsaved editor state through the same component the
 * learner sees.
 *
 * The pill is a disclosure button rather than a link to the original, even
 * though it names one. A control that both navigates and expands cannot work
 * on touch (where the tap has to pick one) or by keyboard (where Enter would
 * navigate away before the list could be read), so the outbound links all live
 * inside the panel, where they are real anchors.
 */
export function SourceAttribution({
  attribution,
}: {
  attribution: ProblemAttribution
}) {
  const { original, others } = attribution
  const [open, setOpen] = useState(false)
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Hover-opened and deliberately-opened popovers behave differently: the
  // first must not steal focus and must close when the pointer wanders off,
  // the second must keep both. Tracking *why* it opened is what separates them.
  const openedByHoverRef = useRef(false)

  const cancelClose = () => {
    if (closeTimerRef.current !== null) {
      clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }
  }

  // A pending close must not fire into an unmounted component — switching
  // problems unmounts the pane while the pointer is still over the pill.
  // Reads the ref rather than calling `cancelClose`, which is a fresh closure
  // every render: depending on it would re-run this on every render and clear
  // the very timer the leave delay depends on.
  useEffect(
    () => () => {
      if (closeTimerRef.current !== null) clearTimeout(closeTimerRef.current)
    },
    [],
  )

  // Guarded on `pointerType` because a touch tap fires a synthetic pointer
  // enter before its click. Without the guard the tap opened the panel by
  // "hover" and the click that followed toggled it straight back shut, so the
  // panel flashed and vanished on every mobile tap.
  const openByHover = (event: React.PointerEvent) => {
    if (event.pointerType !== 'mouse') return
    cancelClose()
    if (!open) openedByHoverRef.current = true
    setOpen(true)
  }

  // Clicking a panel the pointer already opened pins it rather than closing
  // it: the click means "keep this", and Radix's trigger would read the
  // already-open state as a request to toggle shut. Preventing default is how
  // a composed Radix handler is opted out of.
  const pinIfHoverOpened = (event: React.MouseEvent) => {
    if (!open || !openedByHoverRef.current) return
    // Cancel first. On touch, `pointerleave` fires before `click`, so a tap
    // that pins an already-hover-open panel has just armed a close timer;
    // clearing the flag stops `onCloseAutoFocus` reading it but does nothing
    // about the timer, which would shut the panel 150ms after the pin.
    cancelClose()
    openedByHoverRef.current = false
    event.preventDefault()
  }

  // Gated on `open` as well as the flag, because the flag outlives the panel
  // it described: the hover-close timer calls `setOpen` directly, so Radix
  // never runs `onOpenChange` and never clears it. Without the `open` check, a
  // touch tap after any earlier mouse hover would arm a timer off that stale
  // flag — `pointerleave` fires before `click` on touch — and the timer would
  // fire 150ms into the freshly opened panel.
  const scheduleClose = (event: React.PointerEvent) => {
    // Mouse only, mirroring `openByHover`. A leave delay is a mouse affordance
    // — there is no hover on touch — and the synthetic `pointerleave` a tap
    // fires must not arm a timer against the panel that same tap is opening.
    if (event.pointerType !== 'mouse') return
    if (!open || !openedByHoverRef.current) return
    cancelClose()
    closeTimerRef.current = setTimeout(() => setOpen(false), LEAVE_DELAY_MS)
  }

  // A source that is credited but has nowhere to link (a book) still counts:
  // the credit itself is the point, so presence — not linkability — decides.
  if (!hasAttribution(attribution)) return null

  // Two shapes, because the two cases make different statements.
  //
  // With an original to credit, the pill *names* it and `+N` counts everything
  // else. Crediting is the whole point there, and a bare mark credits nobody —
  // a reader who does not recognise the favicon learns nothing from it.
  //
  // With none, BCTCI posed the question and the row is only pointing at other
  // places to practise it. A name is then noise on a line already reading
  // "Also on", so the marks go up alone and overlapped, per design 1a (Mike,
  // LG-409). Nothing is lost: every source is still listed in the panel one
  // hover away, and every one of them is named in `accessibleName` below —
  // which is the only copy a screen reader gets, since `AuthoredIcon` renders
  // each mark decoratively by contract.
  const cluster = original ? [] : others.slice(0, MAX_CLUSTER_MARKS)
  const extraCount = original ? others.length : others.length - cluster.length
  const label = original ? 'Source' : 'Also on'

  // `+N` is a *visual* overflow, so it must not be the accessible story too:
  // the cluster names nobody on screen, and a screen reader hearing "and 2
  // more" after no names at all would be told the count of something it was
  // never told the identity of. Every source is spelled out instead.
  const accessibleName = original
    ? `Source attribution: ${original.name}${
        extraCount > 0 ? ` and ${extraCount} more` : ''
      }`
    : `Source attribution: also on ${others
        .map((source) => source.name)
        .join(', ')}`

  return (
    <Popover.Root
      open={open}
      onOpenChange={(next) => {
        // Unconditional: a deliberate open must not inherit a close timer left
        // armed by a pointer that has already wandered off, or the panel opens
        // and then shuts itself 150ms later.
        cancelClose()
        // Set on opens only. Radix drives this for click, Escape and
        // outside-click; clearing it on a click-*open* is what stops a
        // click-opened popover closing on mouseleave. Clearing it on a *close*
        // would be wrong: `onCloseAutoFocus` runs after this and still needs to
        // know how the panel that is closing was opened, or Escape would yank
        // focus onto the pill out of a hover-opened panel.
        if (next) openedByHoverRef.current = false
        setOpen(next)
      }}
    >
      <Popover.Trigger asChild>
        <button
          type="button"
          aria-label={accessibleName}
          onPointerEnter={openByHover}
          onPointerLeave={scheduleClose}
          onClick={pinIfHoverOpened}
          onFocus={cancelClose}
          className="text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:ring-ring inline-flex shrink-0 items-center gap-1.5 rounded-full px-2 py-1 text-sm focus-visible:ring-2 focus-visible:outline-none"
        >
          <span>{label}:</span>
          {original ? (
            <>
              <AuthoredIcon
                icon={original.icon}
                size="sm"
                className="shrink-0"
                fallbackChip
              />
              <span className="max-w-40 truncate font-medium">
                {original.name}
              </span>
            </>
          ) : (
            /* `-space-x-1` is what makes this a cluster rather than a queue,
               and the ring is what keeps the overlapped marks legible against
               each other. `ring-background` rather than design 1a's literal
               white, so the separator follows the theme instead of glowing in
               dark mode. Circles because the overlap has to read as a stack at
               14px, and a shape everyone already knows from avatar rows does
               that; the marks keep `AuthoredIcon`'s `object-contain`, so a
               logo that is not quite square letterboxes rather than losing an
               edge to the crop. A source with no icon still takes its slot as
               a muted chip — it is one more place the problem can be
               practised, and the panel is where its name lives. */
            <span className="flex shrink-0 items-center -space-x-1">
              {cluster.map((source) => (
                <AuthoredIcon
                  key={source.id}
                  icon={source.icon}
                  size="sm"
                  className="ring-background rounded-full ring-2"
                  fallbackChip
                />
              ))}
            </span>
          )}
          {extraCount > 0 && <span>+{extraCount}</span>}
          <ChevronDown aria-hidden="true" className="h-3.5 w-3.5" />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          // Radix gives the panel `role="dialog"`; without a name a keyboard
          // user who opens it deliberately hears only "dialog".
          aria-label="Source attribution"
          side="bottom"
          align="end"
          sideOffset={6}
          onOpenAutoFocus={(event) => {
            // Yanking focus off the page because a pointer drifted over the
            // pill would scroll the reader somewhere they did not ask to go.
            if (openedByHoverRef.current) event.preventDefault()
          }}
          onCloseAutoFocus={(event) => {
            // Radix's non-modal close pulls focus back to the trigger. That is
            // right after a deliberate open, and wrong after a hover: the
            // pointer drifting across the pill while someone types in the code
            // editor would open the panel, close it 150ms later, and land
            // focus on the pill — their next keystrokes go nowhere.
            if (openedByHoverRef.current) event.preventDefault()
          }}
          onPointerEnter={cancelClose}
          onPointerLeave={scheduleClose}
          className="border-border bg-popover text-popover-foreground z-50 w-72 rounded-lg border p-2 shadow-lg"
        >
          {original && (
            <section>
              <h3 className="text-muted-foreground px-2 pt-1 pb-1 text-[11px] font-semibold tracking-wide uppercase">
                Attribution
              </h3>
              <SourceRow source={original} isOriginal />
            </section>
          )}
          {others.length > 0 && (
            <section>
              <h3 className="text-muted-foreground px-2 pt-2 pb-1 text-[11px] font-semibold tracking-wide uppercase">
                Also available on
              </h3>
              {others.map((source) => (
                <SourceRow key={source.id} source={source} isOriginal={false} />
              ))}
            </section>
          )}
          <p className="border-border text-muted-foreground mt-2 border-t px-2 pt-2 text-[11px] leading-snug">
            {DISCLAIMER}
          </p>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}

/**
 * A problem's attribution, and whether the copyright notice may be printed.
 *
 * Two things on the description panel need this: the widget, and the notice,
 * which disappears entirely when the problem originated elsewhere. They are
 * returned together because the second is a function of the first — leaving
 * consumers to derive it is how the panel and the admin preview grew two
 * copies of the rule.
 *
 * `enabled: false` means the caller deliberately chose not to ask — an
 * interview candidate, who should not be shown where the problem came from.
 * The scope then resolves the same way as any other unknown, which is a
 * constant answer and so tells them nothing.
 *
 * This is a UI courtesy, **not** a security boundary, and must not be relied on
 * as one. `problems.getSources` is a `publicProcedure` a candidate can call
 * directly, and `isInterviewer` derives from a client-writable Y.js map. The
 * cache leak the mask below closes was the third way in, and closing it buys
 * consistency rather than secrecy — it does not make the other two any
 * narrower. The problem statement itself already ships in the bundle
 * regardless — a caveat the ticket accepts — so provenance is no more exposed
 * than the problem is. Enforcing it properly means gating the procedure
 * server-side on interview membership.
 */
export function useProblemAttribution(
  slug: string | null | undefined,
  options: { enabled?: boolean } = {},
): {
  attribution: ProblemAttribution | null
  copyrightScope: AttributionCopyrightScope
} {
  const enabled = (options.enabled ?? true) && !!slug
  const { data } = trpc.problems.getSources.useQuery(
    { slug: slug ?? '' },
    { enabled },
  )
  // `enabled: false` stops the *fetch*, not the read: TanStack hands back a
  // cache entry any earlier render of this slug left behind, and the app's
  // `QueryClient` is a module-level singleton, so a candidate who practised
  // this problem before joining the interview arrives with one. Without the
  // mask the copyright notice would then follow the problem's real scope for
  // that candidate and the withheld default for everyone else — the same
  // interview showing or hiding the notice depending on where the reader had
  // been earlier in the session.
  const attribution = enabled ? (data ?? null) : null
  return { attribution, copyrightScope: copyrightScopeFor(attribution) }
}
